Logical view to parse URL query params and collect data for a new session
<template>
	<!-- Footer notifications -->
	<BaseAlertList class="bottom-26!">
		<template v-if="showErrors">
			<BaseAlert
				type="warning"
				v-for="error in validationErrors"
				:key="error"
			>
				{{ error }}
			</BaseAlert>
			<!-- Non-blocking warnings -->
			<BaseAlert
				type="warning"
				v-for="warning in nonBlockingWarnings"
				:key="warning"
			>
				{{ warning }}
			</BaseAlert>
		</template>
	</BaseAlertList>

	<form
		@submit.prevent="onSubmit"
		ref="form"
		aria-label="New Session Form"
	>
		<!-- Main grid has three rows: header, columns, and footer -->
		<div
			class="grid h-dvh w-screen grid-rows-[max-content_minmax(0,1fr)_max-content]"
		>
			<HeaderNav sync />

			<!-- Two column layout -->
			<div
				class="grid h-full w-screen grid-cols-[minmax(0,1fr)_255px] grid-rows-[100%] overflow-x-visible bg-gray-50 p-5 pr-0 pb-0"
			>
				<!-- Left column -->
				<div class="-mr-2 w-full overflow-y-scroll bg-slate-50 py-4 pr-2">
					<div
						v-auto-animate
						v-if="plans"
					>
						<h1 class="pb-5 font-serif text-4xl font-normal italic">
							{{ t('newSession.plansTitle') }}
						</h1>
						<UnusedParticipantWarnings
							v-if="
								plans[0]?.leadId && cloudSyncEnabled && unusedParticipantsLoaded
							"
							:plans="plans"
							:unusedParticipants="unusedParticipants"
							@add-plan="addPlanFromWarning"
						/>
						<FormKit
							type="repeater"
							:actions="false"
							v-model="plans"
							dynamic
							validation="min:1"
							min="1"
							max="10"
							id="plan-list"
							:add-button="false"
							:insert-control="false"
							:up-control="false"
							:down-control="false"
							content-class="w-full flex flex-col"
							item-class="rounded test-id-plan-item"
							#default="{ value: itemValue, index }"
						>
							<ParticipantCard
								:appointment-id="appointmentId"
								:autoload-latest-plan="autoloadLatestPlan[index] ?? true"
								:cloud-sync-enabled="cloudSyncEnabled"
								:index="index"
								:plan="itemValue as Partial<PlanData>"
								:selectedPriceBook="selectedPriceBook"
								v-model:selectedPlan="selectedPlansRef[index]"
								@create-new-plan="handleCreateNewPlan"
								@selected-plan-change="handleSelectedPlanChange"
							/>
						</FormKit>
						<!-- Add new participant button -->
						<div class="group my-7 flex justify-center">
							<button
								type="button"
								aria-label="add-participant-button"
								class="text-accent-500 group-hover:text-accent-600 flex font-bold"
								@click="addParticipant()"
							>
								<PlusCircleIcon
									class="mr-1 size-6"
									aria-hidden="true"
								/>
								{{ t('newSession.addPlan') }}
							</button>
						</div>
					</div>

					<RadioGroup
						v-model="sessionParams.FuneralHomeLocationId"
						@update:modelValue="handleFuneralHomeChange"
					>
						<div class="flex flex-row gap-3 overflow-x-auto py-5">
							<SelectFuneralHomeCard
								class="w-32"
								v-for="location in visibleSessionLocations"
								:funeral-home="location"
								:price-book-id="priceBookIdFromLocation(location)"
								:has-pricebook-error="isFhLocationMissingPricebook(location)"
							/>

							<button
								v-if="hasFuneralHomesWithPricebookErrors"
								type="button"
								class="group flex min-h-30 w-32 shrink-0 items-center justify-center gap-2 self-center rounded-lg bg-white px-2 py-5 font-bold text-blue-500 shadow-sm"
								:aria-expanded="showAllFuneralHomes"
								@click="showAllFuneralHomes = !showAllFuneralHomes"
							>
								<component
									:is="showAllFuneralHomes ? MinusIcon : PlusIcon"
									class="size-5"
									aria-hidden="true"
								/>
								<span class="group-hover:underline">
									{{ showAllFuneralHomes ? t('newSession.showLess') : t('newSession.showAll') }}
								</span>
							</button>
						</div>
					</RadioGroup>
				</div>

				<!-- Right column -->
				<DeckPicker
					class="mr-2 h-full overflow-hidden"
					v-model="deckChoice"
				/>
			</div>

			<footer class="flex h-20 w-full items-center justify-center bg-white">
				<button
					type="submit"
					class="rounded-lg border-0 px-40 py-2.5 text-base font-bold"
					:class="[
						validationErrors.length === 0
							? 'bg-accent-400 text-accent-700 hover:bg-accent-700 hover:text-white'
							: 'text-secondary-400 bg-slate-200',
					]"
				>
					{{
						resumePresentation ? t('newSession.resume') : t('newSession.submit')
					}}
				</button>
			</footer>

			<PrecoaModal
				class="sm:w-auto!"
				:show="showAddParticipantModal"
				@close="showAddParticipantModal = false"
			>
				<AddParticipantModalContent
					:contacts="unusedContacts"
					@cancel="showAddParticipantModal = false"
					@contact-selected="addNewCard"
				/>
			</PrecoaModal>
			<PrecoaModal
				:show="priceBookMismatch && !overridePBMismatch"
				@close="resetFuneralHomeSelection"
			>
				<div aria-label="PB Mismatch Modal">
					<h1
						class="mt-4 text-center text-base leading-normal font-bold text-slate-900"
					>
						{{ t('newSession.switchFuneralHomeTitle') }}
					</h1>
					<p class="mt-2 px-5 text-center text-sm text-slate-500">
						{{ t('newSession.switchFuneralHomeSubtitle') }}
					</p>
				</div>
				<template #buttons>
					<div class="flex w-full flex-col gap-6 pb-2">
						<button
							aria-label="Switch Funeral Home"
							type="submit"
							class="font-medium text-red-500 underline"
							@click.prevent="overridePBMismatch = true"
						>
							{{ t('newSession.switchFuneralHomeButton') }}
						</button>
						<button
							aria-label="Cancel Funeral Home Selection"
							type="submit"
							class="bg-accent-400 text-accent-700 hover:bg-accent-700 rounded-lg border-0 px-16 py-3 text-base font-bold hover:text-white"
							@click.prevent="resetFuneralHomeSelection"
						>
							{{ t('general.cancel') }}
						</button>
					</div>
				</template>
			</PrecoaModal>
		</div>
	</form>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import type { PriceBook } from '@/classes/PriceBook'
import { Session } from '@/classes/Session'
import CrmAppointmentImporter from '@/classes/importers/crmAppointmentImporter'
import SalesforceAppointmentImporter from '@/classes/importers/salesforceAppointmentImporter'
import BaseAlert from '@/components/common/BaseAlert.vue'
import BaseAlertList from '@/components/common/BaseAlertList.vue'
import HeaderNav from '@/components/common/HeaderNav.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import SelectFuneralHomeCard from '@/components/common/SelectFuneralHomeCard.vue'
import DeckPicker from '@/components/home/decks/DeckPicker.vue'
import { DeckFlavor, flavorLanguages, Language } from '@/enums/Language'
import { type PlanVersion, usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import { type Contact } from '@/services/CloudSync/cloudSyncPlanService'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import {
	cardWasEdited,
	getUnusedContacts,
	planToContact,
} from '@/services/contactHelper'
import { Feature, useFeatureService } from '@/services/featureService'
import { useContentStore } from '@/stores/content'
import { useCrmStore } from '@/stores/crm'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import type { CombinedFhLocation, PlanData, SessionData } from '@/types'
import AddParticipantModalContent from '@/views/NewSession/AddParticipantModalContent.vue'
import ParticipantCard from '@/views/NewSession/ParticipantCard.vue'
import UnusedParticipantWarnings from '@/views/NewSession/UnusedParticipantWarnings.vue'
import { FormKit } from '@formkit/vue'
import { RadioGroup } from '@headlessui/vue'
import { MinusIcon, PlusIcon } from '@heroicons/vue/24/outline'
import PlusCircleIcon from '@heroicons/vue/24/solid/PlusCircleIcon'
import { captureException } from '@sentry/vue'
import { asyncComputed } from '@vueuse/core'
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps({
	appointmentId: String,
	sessionId: String,
	deckId: String,
})

const { t } = useI18n()
const form = ref()
const featureService = useFeatureService()
const sessionParams = ref<Partial<SessionData>>({})
const plans = ref<Partial<PlanData>[]>([])
const planPrototype = ref<Partial<PlanData>>({}) // No active session yet so we need to build a planPrototype
const cloudSyncEnabled = featureService.isEnabled(Feature.CloudSync)
const selectedPlansRef = ref<Array<string | undefined>>([])
const selectedPlansSourceRef = ref<Record<string, string>>({})
const autoloadLatestPlan = ref<boolean[]>([])
const showErrors = ref(false)
const unusedParticipants = ref<Record<string, PlanVersion>>()
const unusedParticipantsLoaded = ref<boolean>(false)
const sessionStore = useSessionStore()
const plansStore = usePlanStore()
const content = useContentStore()
const router = useRouter()
const crm = useCrmStore()
const cloudSyncService = useCloudSyncService()
const errorMessage = ref('')
const language = ref(Language.English)
const existingSession = ref<Session | undefined>()
const showAddParticipantModal = ref(false)
const contactList = ref<Contact[]>([])
const overridePBMismatch = ref(false)
const cloudSyncInitialSessionUpdate = ref(true)
const versionHelper = usePlanVersionsV2()

defineExpose({
	plans,
	sessionParams,
})

// Send the language ref to the deck repeater so we can update it ourselves
provide('language', language)

/** Get currently selected funeral home */
const selectedFuneralHome = computed(() => {
	if (!allSessionLocations.value) return undefined
	return allSessionLocations.value.find(
		(loc) => loc.id === sessionParams.value.FuneralHomeLocationId
	)
})

const unusedContacts = computed(() => {
	return getUnusedContacts(plans.value, contactList.value ?? [])
})

const selectedPriceBook = computed<PriceBook | undefined>(() => {
	const funeralHomeId = sessionParams.value.FuneralHomeLocationId
	return funeralHomeId
		? crm.findPriceBookForFuneralHome(funeralHomeId)
		: undefined
})

const priceBookMismatch = computed(() => {
	const anyPlanHasCartItems = plans.value.some(
		(plan) =>
			(plan.cartItems?.length ?? 0) > 0 ||
			(plan.customCartItems?.length ?? 0) > 0
	)

	if (!anyPlanHasCartItems) {
		return false
	}

	return plans.value.some((plan) => {
		const planPriceBook = plan.funeralHomeId
			? crm.findPriceBookForFuneralHome(plan.funeralHomeId)
			: undefined

		if (!planPriceBook) {
			return false
		}

		return selectedPriceBook.value?.data.id !== planPriceBook.data.id
	})
})

/** Array of validation error messages for the user */
const validationErrors = computed<string[]>(() => {
	const errors: string[] = []
	if (!sessionParams.value.slideDeckId) {
		errors.push(t('newSession.errors.noDeck'))
	}
	if (!plans.value || plans.value?.length === 0) {
		errors.push(t('newSession.errors.noPlans'))
	}
	if (!plans.value?.every((p) => !!p.firstName)) {
		errors.push(t('newSession.errors.noFirstName'))
	}
	if (!sessionParams.value.FuneralHomeLocationId) {
		errors.push(t('newSession.errors.noFuneralHome'))
	}
	return errors
})

/** Non-blocking warnings */
const nonBlockingWarnings = computed<string[]>(() => {
	const warnings: string[] = []

	if (
		selectedFuneralHome.value &&
		isFhLocationMissingPricebook(selectedFuneralHome.value)
	) {
		warnings.push(t('newSession.errors.noPriceBook'))
	}
	return warnings
})

/** Computed property for getting/setting the deck by uniqueId */
const deckChoice = computed<string | undefined>({
	get: () =>
		sessionParams.value.language && sessionParams.value.slideDeckId
			? sessionParams.value.language + ':' + sessionParams.value.slideDeckId
			: undefined,
	set: (value: string | undefined) => {
		if (!value) {
			sessionParams.value.language = undefined
			sessionParams.value.slideDeckId = undefined
			return
		}

		const [language, id] = value.split(':')
		console.assert(
			Object.values(DeckFlavor).includes(language as DeckFlavor),
			'Deck uniqueId has invalid language:',
			language
		)
		sessionParams.value.language = language as DeckFlavor
		sessionParams.value.slideDeckId = id
	},
})

const resumePresentation = computed<boolean>(() => {
	return plans.value.some((plan) => !!plan.lastUpdated)
})

watch(plans, async (newValue) => {
	// don't update until we have done the first automatic load of unused participants
	if (
		(plans.value[0]?.leadId || plans.value[0]?.opportunityId) &&
		unusedParticipantsLoaded.value &&
		cloudSyncEnabled
	) {
		unusedParticipants.value = await versionHelper.getUnusedParticipants(
			{
				leadId: plans.value[0]?.leadId,
				opportunityId: plans.value[0]?.opportunityId,
			},
			newValue
		)
	}
})

// When we arrive at this page, attempt
// to import data that was specified in the query string.
// If the query string specifies something we are unable to import,
// we must show an error.
onMounted(async () => {
	// Select a deck if one was provided
	if (props.deckId) {
		selectDeck(props.deckId)
	}

	// Import appointment if an id was provided
	if (props.appointmentId) {
		// First, try to find an existing session for the appointment
		const matchedSession = sessionStore.getSessionForAppointment(
			props.appointmentId
		)

		if (matchedSession) {
			existingSession.value = matchedSession
		} else {
			// Otherwise, import the data from the appointment for a new session
			await importAppointment(props.appointmentId)
		}

		// Import a previous session by id
	} else if (props.sessionId) {
		existingSession.value = sessionStore.sessions.find(
			(session) => session.id === props.sessionId
		)
	}

	contactList.value = await sessionStore.getContacts(
		props.appointmentId,
		existingSession.value
	)

	if (existingSession.value) {
		// Set the sessionParams after the nextTick to ensure that the reactive
		// reference is updated before the FormKit repeater is updated.
		nextTick(async () => {
			plans.value = await plansStore.loadPlans(existingSession.value!.planIds)
			sessionParams.value = {
				...existingSession.value!.serialize(),
			}

			// TODO make plan prototype from single function that doesn't change based on existing sessions
			// New blank cards inherit appointment-level IDs (not contactId — that belongs to a person)
			// If we create a new card based on a particular person, it's done through PlanToContact
			planPrototype.value.leadId = plans.value[0]?.leadId
			planPrototype.value.opportunityId = plans.value[0]?.opportunityId
			selectDeck(
				(existingSession.value!.language as string) +
					':' +
					existingSession.value!.slideDeckId
			)
		}).finally(async () => await addUnusedParticipants())
	} else if (!plans.value || plans.value.length === 0) {
		// We are starting a blank session; add one participant
		addParticipant()
	} else {
		await addUnusedParticipants()
	}

	focusOnPlan()
})

/** Check if a location has a pricebook */
function isFhLocationMissingPricebook(location: CombinedFhLocation) {
	return !crm.findPriceBookForFuneralHome(location?.id)
}

const priceBookIdFromLocation = (location: CombinedFhLocation) => {
	return crm.findPriceBookForFuneralHome(location.id)?.data.id
}

/** Create a new, empty participant */
const addParticipant = () => {
	if (unusedContacts.value.length) {
		// open the modal for add participant
		showAddParticipantModal.value = true
	} else {
		addNewCard('new')
	}
}

const addNewCard = (contact: Contact | Partial<PlanData> | 'new') => {
	showAddParticipantModal.value = false

	if (!plans.value) plans.value = []

	if (contact === 'new') {
		plans.value.push(new Plan(planPrototype.value))
	} else {
		plans.value.push(new Plan(contact))
	}

	focusOnPlan(true)
}

const getLocalPlan = async (
	plan: Partial<PlanData>
): Promise<Plan | undefined> => {
	if (!plan.id) {
		return undefined
	}

	try {
		return await plansStore.loadPlan(plan.id)
	} catch {
		return undefined
	}
}

/**
 * Handles form submission.
 * Displays validation errors if present.
 * Updates or creates a session in sessionStore, then navigates to the presentation.
 */
async function onSubmit() {
	// Making a Set so we can check that each plan.id is unique
	const selectPlansSet = new Set(plans.value.map((plan) => plan.id))
	if (plans.value.length !== selectPlansSet.size) {
		validationErrors.value.push(t('newSession.duplicatePlanSelectionError'))
	}
	if (validationErrors.value.length > 0) {
		showErrors.value = true
		return
	}

	// planIds for the session has been gathered, no duplication
	// if the plan is from cloudSync, overwrite the encryption keys
	for (const plan of plans.value) {
		if (selectedPlansSourceRef.value[plan.id!] === 'cloud-sync') {
			await cloudSyncService.plan.loadEncryptionKeyById(plan.id!)
		}
	}

	const planObjects = []
	for (const plan of plans.value) {
		// record the current participant card content to compare with original plan, see if there's edits
		const cardInfo = planToContact(plan)
		let originalPlan
		let planData

		if (selectedPlansSourceRef.value[plan.id!] === 'cloud-sync') {
			// if plan is from cloudSync, retain the contact edits made on this page
			// while hydrating the rest of the plan.
			// Decryption is safe here because the keys are updated above
			originalPlan = await cloudSyncService.plan.getById(plan.id!)

			planData = {
				...planPrototype.value,
				...originalPlan,
				...cardInfo,
				...defaultPlanDetails(),
				funeralHomeId: sessionParams.value.FuneralHomeLocationId,
			}
		} else {
			originalPlan = await getLocalPlan(plan)

			planData = {
				...planPrototype.value,
				...plan,
				...defaultPlanDetails(),
				funeralHomeId: sessionParams.value.FuneralHomeLocationId,
			}
		}

		// If AFP made changes on the contact cards before starting session
		// or there is no updated at time we want to set the lastUpdated timestamp
		// so cloudSync accepts the upsert
		if (
			!planData.lastUpdated ||
			(originalPlan && cardWasEdited(originalPlan!, cardInfo))
		) {
			planData.lastUpdated = new Date().toISOString()
		}
		planObjects.push(new Plan(planData))
	}

	// If there is an existing session, update it in the store and navigate to it
	if (existingSession.value) {
		resumeSession(planObjects)
	}
	// If there is no existing session, begin a new one
	else {
		await beginSession(planObjects)
	}
}

async function resumeSession(planObjects: Plan[]) {
	if (!existingSession.value) {
		throw new Error('Cannot resume missing session')
	}

	// Save all existing plans to the plans store
	for (const planData of planObjects) {
		// Update the plan in the plans store
		await plansStore.savePlan(new Plan(planData))
	}

	// Update the existing session with new data
	Object.assign(existingSession.value, sessionParams.value)

	existingSession.value.planIds = planObjects.map((plan) => plan.id)

	await sessionStore.resumeSession(existingSession.value)
}

/** Create the new session and start it */
async function beginSession(planObjects: Plan[]) {
	// First, create and store all plans in the plans store
	const planIds: string[] = []

	if (!plans.value || plans.value.length === 0) {
		throw new Error('Cannot start a session with no plans')
	}

	for (const plan of planObjects) {
		// Add to plans store
		await plansStore.addPlan(plan)
		planIds.push(plan.id)
	}

	// Create session with plan IDs
	const sessionData: Partial<SessionData> = {
		...sessionParams.value,
		planIds,
	}

	const session = new Session(sessionData)

	await sessionStore.startSession(session)
	await router.push({ name: 'present', params: { sessionId: session.id } })
}

/** Focus on the first name of the first or last plan */
async function focusOnPlan(focusLast: boolean = false) {
	// Focus the first name of the first plan
	await nextTick()
	const inputs = form.value.querySelectorAll('input[name="firstName"]')

	const target = focusLast ? inputs[inputs.length - 1] : inputs[0]
	target?.focus()
}

const handleSelectedPlanChange = async ({
	index,
	plan,
	source,
}: {
	index: number
	plan: Partial<PlanData>
	source: string
}) => {
	if (cloudSyncInitialSessionUpdate.value) {
		sessionParams.value.FuneralHomeLocationId = plan.funeralHomeId
		cloudSyncInitialSessionUpdate.value = false
	}

	// Record the source of the selected plan
	selectedPlansSourceRef.value[plan.id!] = source
	// Replace the plans with that planData
	Object.assign(plans.value[index], plan)
	autoloadLatestPlan.value[index] = false
}

const handleCreateNewPlan = async ({
	index,
	plan,
}: {
	index: number
	plan: Partial<PlanData>
}) => {
	const newPlan = new Plan(planToContact(plan))
	selectedPlansRef.value[index] = newPlan.id
	Object.assign(plans.value[index], newPlan)
}

/**
 * Adds Plans from CloudSync that are not in the local saved session
 * Make sure to only call this once in onMounted
 */
const addUnusedParticipants = async () => {
	if (!cloudSyncEnabled) {
		return
	}

	const leadId = plans.value[0]?.leadId
	const opportunityId = plans.value[0]?.opportunityId

	let unused
	if (leadId || opportunityId) {
		unused = await versionHelper.getUnusedParticipants(
			{
				leadId: leadId,
				opportunityId: opportunityId,
			},
			plans.value
		)
	}

	if (unused) {
		for (const participant of Object.values(unused)) {
			const planData =
				await versionHelper.getPlanBasicDataForVersion(participant)
			if (!planData) {
				continue
			}
			addNewCard(planData)
			selectedPlansSourceRef.value[planData.id!] = participant.source
		}
	}

	unusedParticipantsLoaded.value = true
}

/** All the locations appropriate for the current session */
const allSessionLocations = asyncComputed(async () => {
	return await content.getFuneralHomeLocations(
		sessionParams.value.language || DeckFlavor.Default,
		{
			crmGroupId: sessionParams.value.crmFhGroupId,
		}
	)
})

const showAllFuneralHomes = ref(false)

const hasFuneralHomesWithPricebookErrors = computed(() => {
	return (allSessionLocations.value ?? []).some(isFhLocationMissingPricebook)
})

const visibleSessionLocations = computed(() => {
	const locations = allSessionLocations.value ?? []

	return showAllFuneralHomes.value
		? locations
		: locations.filter((location) => !isFhLocationMissingPricebook(location))
})

/** Find the specified appointment and convert into sessionParams */
async function importAppointment(id: string) {
	try {
		const importResult = await getImportedAppointmentResult(id)
		if (importResult) {
			sessionParams.value = importResult.session
			plans.value = importResult.plans
		}
	} catch (error) {
		addParticipant()
	}
}

const getImportedAppointmentResult = async (id: string) => {
	try {
		let importer
		if (useFeatureService().isEnabled(Feature.SalesforceIntegration)) {
			importer = new SalesforceAppointmentImporter(id)
		} else {
			importer = new CrmAppointmentImporter(id)
		}
		const importResult = await importer.import()
		planPrototype.value = importResult.planPrototype
		return importResult
	} catch (error) {
		captureException(error)
		console.error(error)
		errorMessage.value = 'Unable to import appointment: ' + error
		throw error
	}
}

/** Select the specified deck and its language */
function selectDeck(uniqueId: string) {
	deckChoice.value = uniqueId
	language.value =
		flavorLanguages[sessionParams.value.language || DeckFlavor.Default]
}

const addPlanFromWarning = ({
	plan,
	source,
}: {
	plan: Partial<PlanData>
	source: string
}) => {
	addNewCard(plan)
	selectedPlansSourceRef.value[plan.id!] = source
}

const handleFuneralHomeChange = () => {
	if (overridePBMismatch.value && priceBookMismatch.value) {
		overridePBMismatch.value = false
	}
}

const resetFuneralHomeSelection = () => {
	sessionParams.value.FuneralHomeLocationId = plans.value.find(
		(plan) => plan.funeralHomeId
	)?.funeralHomeId
	overridePBMismatch.value = false
}

// Clear cart-related Plan details when changing PBs
const defaultPlanDetails = (): Partial<PlanData> => {
	// If the price book mismatch is true, reset the plan's details to default
	if (overridePBMismatch.value && priceBookMismatch.value) {
		return {
			cartItems: [],
			customCartItems: [],
			completedSteps: [],
			oapIncluded: false,
			everplansIncluded: false,
			paymentStep: 'html',
			oapStep: 'html',
			everplansStep: 'html',
			documents: [],
		}
	}

	return {}
}
</script>
